import { useState, useRef, useEffect } from 'react';

const NAV_ITEMS = [
  { label: 'About', href: '#about' },
  { label: 'How It Works', href: '#how' },
  { label: 'Races', href: '#races' },
  { label: 'Features', href: '#features' },
  { label: 'FAQ', href: '#faq' },
];

const DRAWER_ID = 'landing-nav-drawer';

interface NavBarProps {
  onLoginClick: () => void;
}

export function NavBar({ onLoginClick }: NavBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // SF-138 Component 4: focus trap + Escape + body-scroll-lock, mirroring LoginModal.tsx's
  // already-shipped mechanism (`useEffect`-based, same directory) rather than inventing a second
  // one for the same problem.
  useEffect(() => {
    if (!menuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        return;
      }

      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      hamburgerRef.current?.focus();
    };
  }, [menuOpen]);

  return (
    <header>
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-16 px-6 md:px-12"
        style={{
          background: 'rgba(17, 15, 13, 0.85)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid #24201b',
        }}
      >
        <span
          className="tracking-[0.15em]"
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 20,
            fontWeight: 700,
            color: '#f0e8dc',
          }}
        >
          GALDORN
        </span>

        {/* Desktop nav links */}
        <div className="hidden md:flex gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="transition-colors hover:text-game-50"
              style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#a89889',
                textDecoration: 'none',
              }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Desktop login button */}
          <button
            onClick={onLoginClick}
            className="hidden md:block transition-colors hover:text-game-50 hover:bg-game-800 rounded"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#a89889',
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Log In
          </button>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            onClick={() => setMenuOpen(true)}
            className="md:hidden flex items-center justify-center"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a89889', minWidth: 44, minHeight: 44 }}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls={DRAWER_ID}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile slide-out menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[60]"
          style={{ background: 'rgba(10, 9, 8, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div
            id={DRAWER_ID}
            ref={drawerRef}
            className="absolute top-0 right-0 h-full w-72 flex flex-col p-6"
            style={{ background: '#110f0d' }}
          >
            <button
              ref={closeButtonRef}
              onClick={() => setMenuOpen(false)}
              className="self-end flex items-center justify-center mb-6"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a89889', fontSize: 24, minWidth: 44, minHeight: 44 }}
              aria-label="Close menu"
            >
              &times;
            </button>
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 px-2 rounded transition-colors hover:bg-game-800"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    fontSize: 16,
                    fontWeight: 500,
                    color: '#a89889',
                    textDecoration: 'none',
                    minHeight: 48,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {item.label}
                </a>
              ))}
            </div>
            <div className="mt-auto">
              <button
                onClick={() => { setMenuOpen(false); onLoginClick(); }}
                className="w-full py-3 rounded transition-colors hover:bg-game-800"
                style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#a89889',
                  background: 'none',
                  border: '1px solid #2e2924',
                  cursor: 'pointer',
                  minHeight: 48,
                }}
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
